import { useState, useEffect } from "react";
import { Award, Download, Eye, X, Loader2, Star } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { api } from "../utils/apiComponent/apiClient";
import { endpoints } from "../utils/endpoints";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { useTranslation } from "../i18n";
import { CourseReviewModal } from "./CourseReviewModal";

/**
 * Composant réutilisable pour afficher et télécharger un certificat
 * Inspiré de ResourcePreview.tsx
 */
export const CertificatePreview = ({
  certificate,
  onDownload,
  showEvaluationModal = false,
  onEvaluationComplete
}) => {
  const { tObj } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(showEvaluationModal);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Vérifier si l'utilisateur peut télécharger le certificat
  const canDownload = certificate?.canDownloadCertificate ?? true;
  const hasRated = certificate?.hasRated ?? false;
  const hasCommented = certificate?.hasCommented ?? false;
  // L'utilisateur doit avoir noté ET commenté pour pouvoir télécharger
  const needsEvaluation = !hasRated || !hasCommented;
  // L'utilisateur peut accéder au certificat seulement s'il a noté ET commenté ET que canDownloadCertificate est true
  const canAccessCertificate = hasRated && hasCommented && canDownload;

  const handleDownload = async () => {
    // Si l'utilisateur n'a pas encore évalué le cours (note ET commentaire), afficher le modal d'évaluation
    if (needsEvaluation || !canDownload) {
      setShowReviewModal(true);
      return;
    }

    setIsDownloading(true);
    try {
      if (onDownload) {
        await onDownload(certificate);
      } else {
        // Téléchargement par défaut
        const certificateId = certificate.id;
        
        if (!certificateId) {
          throw new Error(tObj.certificate.certificateIdMissing?.() || 
            "ID du certificat manquant");
        }
        
        const blob = await api.get(endpoints.downloadCertificate(certificateId), {
          responseType: 'blob'
        });
        
        if (!blob || !(blob instanceof Blob)) {
          throw new Error(tObj.certificate.invalidFile?.() || 
            "Fichier invalide");
        }
        
        if (blob.size === 0) {
          throw new Error(tObj.certificate.emptyFile?.() || 
            "Fichier vide");
        }
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const courseTitle = certificate.courseTitle || 
          certificate.course?.title || 
          'Certificat';
        const year = certificate.issuedAt 
          ? new Date(certificate.issuedAt).getFullYear() 
          : new Date().getFullYear();
        const cleanTitle = courseTitle.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_');
        link.download = `Certificat_${cleanTitle}_${year}.pdf`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccessToast({ 
          message: tObj.certificate.downloadSuccess?.() || 
          "Téléchargement lancé avec succès" 
        });
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement du certificat:", error);
      const errorMessage = error?.message || 
        tObj.certificate.downloadError?.() || 
        "Une erreur est survenue lors du téléchargement";
      showErrorToast({ message: errorMessage });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleReviewSubmitted = () => {
    setShowReviewModal(false);
    if (onEvaluationComplete) {
      onEvaluationComplete();
    }
    // Après l'évaluation, permettre le téléchargement
    // Le composant parent devra recharger les données du certificat
  };

  const courseId = certificate?.courseId || certificate?.course?.id;
  const courseTitle = certificate?.courseTitle || certificate?.course?.title;

  // Charger le PDF quand le modal de preview s'ouvre (seulement si l'utilisateur a évalué)
  useEffect(() => {
    if (showPreview && certificate?.id && !pdfUrl && !isLoadingPdf && canAccessCertificate) {
      const loadPdf = async () => {
        setIsLoadingPdf(true);
        setPdfError(null);
        try {
          const certificateId = certificate.id;
          const blob = await api.get(endpoints.downloadCertificate(certificateId), {
            responseType: 'blob'
          });
          
          if (!blob || !(blob instanceof Blob)) {
            throw new Error(tObj.certificate.invalidFile?.() || "Fichier invalide");
          }
          
          if (blob.size === 0) {
            throw new Error(tObj.certificate.emptyFile?.() || "Fichier vide");
          }
          
          const url = window.URL.createObjectURL(blob);
          setPdfUrl(url);
        } catch (error) {
          console.error("Erreur lors du chargement du PDF:", error);
          setPdfError(error?.message || tObj.certificate.downloadError?.() || "Erreur lors du chargement du certificat");
        } finally {
          setIsLoadingPdf(false);
        }
      };
      
      loadPdf();
    }
    
    // Nettoyer l'URL du blob quand le modal se ferme
    return () => {
      if (!showPreview && pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    };
  }, [showPreview, certificate?.id, pdfUrl, isLoadingPdf, canAccessCertificate, tObj]);

  return (
    <>
      <Card className="group hover:shadow-lg transition-shadow">
        <div className="relative overflow-hidden">
          {/* Preview/Thumbnail */}
          <div className="w-full h-48 bg-gradient-to-br from-yellow-50 via-yellow-100 to-amber-100 dark:from-yellow-900/20 dark:via-yellow-800/10 dark:to-amber-900/20 flex items-center justify-center relative group-hover:from-yellow-100 group-hover:via-yellow-200 group-hover:to-amber-200 dark:group-hover:from-yellow-900/30 dark:group-hover:via-yellow-800/20 dark:group-hover:to-amber-900/30 transition-all duration-300">
            <Award className="w-16 h-16 text-yellow-400 dark:text-yellow-500" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/80 dark:bg-black/40 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <Award className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Badge si certificat disponible */}
          {certificate && (
            <div className="absolute top-2 right-2">
              <div className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-bold">
                {tObj.certificate.certified?.() || "Certifié"}
              </div>
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-sm line-clamp-2 flex-1">
                {courseTitle || tObj.certificate.certificate?.() || "Certificat"}
              </h3>
              <Award className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
            </div>
            
            {certificate?.certificateNumber && (
              <p className="text-xs text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                {certificate.certificateNumber}
              </p>
            )}
            
            {certificate?.issuedAt && (
              <p className="text-xs text-gray-500">
                {tObj.certificate.issuedOn?.() || "Délivré le"} {new Date(certificate.issuedAt).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}

            {/* Message si évaluation requise */}
            {needsEvaluation && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                {tObj.certificate.evaluationRequired?.() || 
                  "Vous devez évaluer le cours (noter et commenter) avant de télécharger le certificat"}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              {/* Bouton d'évaluation - affiché seulement si l'évaluation est requise */}
              {needsEvaluation && (
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 text-xs bg-yellow-500 hover:bg-yellow-600 text-white"
                  onClick={() => setShowReviewModal(true)}
                  disabled={isDownloading}
                >
                  <Star className="w-3 h-3 mr-1" />
                  {tObj.certificate.evaluateCourse?.() || "Évaluer le cours"}
                </Button>
              )}
              
              {/* Boutons Aperçu et Télécharger - affichés seulement si l'utilisateur a évalué */}
              {!needsEvaluation && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => setShowPreview(true)}
                    disabled={isDownloading}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    {tObj.certificate.preview?.() || "Aperçu"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleDownload}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                    ) : (
                      <>
                        <Download className="w-3 h-3 mr-1" />
                        {tObj.certificate.download?.() || "Télécharger"}
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal - Seulement si l'utilisateur a évalué le cours */}
      {showPreview && certificate && canAccessCertificate && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPreview(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {tObj.certificate.certificatePreview?.() || "Aperçu du certificat"}
              </h2>
              <button
                onClick={() => {
                  if (pdfUrl) {
                    window.URL.revokeObjectURL(pdfUrl);
                    setPdfUrl(null);
                  }
                  setShowPreview(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Affichage du PDF */}
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                {isLoadingPdf && (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-[#141835] animate-spin mx-auto mb-4" />
                      <p className="text-gray-600">{tObj.certificate.loading?.() || "Chargement du certificat..."}</p>
                    </div>
                  </div>
                )}
                
                {pdfError && (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center p-6">
                      <p className="text-red-600 mb-4">{pdfError}</p>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          setPdfError(null);
                          setIsLoadingPdf(true);
                          try {
                            const certificateId = certificate.id;
                            const blob = await api.get(endpoints.downloadCertificate(certificateId), {
                              responseType: 'blob'
                            });
                            
                            if (!blob || !(blob instanceof Blob)) {
                              throw new Error(tObj.certificate.invalidFile?.() || "Fichier invalide");
                            }
                            
                            if (blob.size === 0) {
                              throw new Error(tObj.certificate.emptyFile?.() || "Fichier vide");
                            }
                            
                            const url = window.URL.createObjectURL(blob);
                            setPdfUrl(url);
                          } catch (error) {
                            setPdfError(error?.message || tObj.certificate.downloadError?.() || "Erreur lors du chargement du certificat");
                          } finally {
                            setIsLoadingPdf(false);
                          }
                        }}
                      >
                        {tObj.certificate.retry?.() || "Réessayer"}
                      </Button>
                    </div>
                  </div>
                )}
                
                {pdfUrl && !isLoadingPdf && !pdfError && (
                  <iframe
                    src={pdfUrl}
                    className="w-full h-[600px] border-0"
                    title={tObj.certificate.certificatePreview?.() || "Aperçu du certificat"}
                  />
                )}
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-3 justify-center mt-4 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  if (pdfUrl) {
                    window.URL.revokeObjectURL(pdfUrl);
                    setPdfUrl(null);
                  }
                  setShowPreview(false);
                  handleDownload();
                }}
                disabled={isDownloading || !canAccessCertificate}
              >
                <Download className="w-4 h-4 mr-2" />
                {tObj.certificate.download?.() || "Télécharger"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && courseId && (
        <CourseReviewModal
          courseId={courseId}
          courseTitle={courseTitle}
          isOpen={showReviewModal}
          onClose={() => {
            if (!needsEvaluation) {
              setShowReviewModal(false);
            }
          }}
          onReviewSubmitted={handleReviewSubmitted}
          required={needsEvaluation || !canDownload}
        />
      )}
    </>
  );
};

